import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Star, MapPin, Briefcase, Award, ShieldCheck, Instagram, Loader2,
  ExternalLink, Clock, CheckCircle2, FileText, Globe, AlertCircle, Send,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useInviteTalentToShift } from './InviteTalentToShiftProvider';
import AddToInviteCartButton from './AddToInviteCartButton';
import CertificationList from './CertificationList';
import { resolveProfilePhoto } from '../utils/profilePhoto';
import { ImageLightbox } from './ImageLightbox';

/**
 * Iter-161 — Reusable talent profile modal surfaced anywhere a profile
 * photo or talent name appears. Fetches the same payload as
 * /talent/{id} (via /api/discover/talent/{id}) and renders the full
 * profile in a single Dialog so admins/clients can vet talent without
 * context-switching to a new page.
 */
const StarRow = ({ value }) => {
  const v = Math.round(Number(value) || 0);
  return (
    <span className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          size={14}
          className={i <= v ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}
        />
      ))}
    </span>
  );
};

const Section = ({ title, icon: Icon, children, testId }) => (
  <section className="space-y-2" data-testid={testId}>
    <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 flex items-center gap-1.5">
      {Icon && <Icon className="w-3.5 h-3.5" />}
      {title}
    </h3>
    {children}
  </section>
);

const TalentProfileModal = ({ talentId, surface, open, onOpenChange }) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [modalLightboxIdx, setModalLightboxIdx] = useState(null);
  const { user } = useAuth();
  const isAdmin = (user?.role || '').toLowerCase() === 'admin';
  // Iter-211 role-aware deep link to the full profile page.
  const _userRole211 = (user?.role || '').toLowerCase();
  const { open: openInviteToShift } = useInviteTalentToShift();

  useEffect(() => {
    let cancelled = false;
    const fetch = async () => {
      if (!open || !talentId) return;
      setLoading(true);
      setError(null);
      setProfile(null);
      try {
        const res = await api.get(`/discover/talent/${talentId}`);
        if (!cancelled) setProfile(res.data);
        // Iter-171 + Iter-227m — log behavioral profile view with
        // surface tag (silent on failure).
        api.post('/metrics/profile-view', {
          talent_id: talentId,
          source: 'modal',
          surface: surface || 'modal',
        }).catch(() => {});
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || 'Could not load profile');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetch();
    return () => { cancelled = true; };
  }, [open, talentId, surface]);

  const renderBody = () => {
    if (loading) {
      return (
        <div className="py-12 flex items-center justify-center" data-testid="profile-modal-loading">
          <Loader2 className="w-7 h-7 animate-spin text-gray-400" />
        </div>
      );
    }
    if (error) {
      return (
        <div className="py-10 text-center" data-testid="profile-modal-error">
          <AlertCircle className="w-9 h-9 text-rose-500 mx-auto mb-2" />
          <p className="text-sm text-gray-700">{error}</p>
        </div>
      );
    }
    if (!profile) return null;

    const name = profile.display_name
      || `${profile.first_name || ''} ${profile.last_initial || ''}`.trim()
      || 'Talent';
    const fullProfilePath = (_userRole211 === 'admin' || _userRole211 === 'super_admin')
      ? `/admin/talent/${profile.user_id}`
      : `/client/discover/${profile.user_id}`;
    const photo = resolveProfilePhoto(profile);
    const location = [profile.city, profile.state].filter(Boolean).join(', ');
    const social = [];
    if (profile.instagram_handle) social.push({
      key: 'instagram',
      label: `@${profile.instagram_handle}`,
      href: `https://instagram.com/${profile.instagram_handle.replace(/^@/, '')}`,
      icon: Instagram,
    });
    if (profile.tiktok_handle) social.push({
      key: 'tiktok',
      label: `@${profile.tiktok_handle}`,
      href: `https://tiktok.com/@${profile.tiktok_handle.replace(/^@/, '')}`,
      icon: Globe,
    });
    const portfolio = (profile.portfolio_images || []).filter(Boolean);
    const certs = profile.certifications || [];
    const work = profile.work_experience || [];
    const reviews = profile.recent_reviews || [];

    return (
      <div className="space-y-6" data-testid="profile-modal-body">
        {/* Header */}
        <div className="flex items-start gap-4">
          {photo ? (
            <img
              src={photo}
              alt={name}
              onClick={() => openModalLightbox(photo)}
              className="w-20 h-20 rounded-full object-cover border-2 border-cyan-100 shrink-0 cursor-zoom-in hover:opacity-90 transition-opacity"
              data-testid="profile-modal-photo"
            />
          ) : (
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-cyan-100 to-cyan-200 flex items-center justify-center shrink-0">
              <span className="text-xl font-semibold text-cyan-700">
                {(name[0] || '?').toUpperCase()}
              </span>
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-lg font-bold text-gray-900 truncate" data-testid="profile-modal-name">{name}</h2>
              {profile.is_top_profile && (
                <Badge
                  className="bg-gradient-to-r from-cyan-500 via-cyan-400 to-emerald-400 text-white border-0 shadow-[0_2px_12px_rgba(6,182,212,0.35)] text-xs font-semibold inline-flex items-center gap-1"
                  data-testid="profile-modal-top-badge"
                >
                  <Star className="w-3 h-3" /> Top Profile
                </Badge>
              )}
              {profile.is_verified && (
                <Badge
                  className="bg-emerald-100 text-emerald-700 border border-emerald-200 text-xs inline-flex items-center gap-1"
                  data-testid="profile-modal-verified-badge"
                >
                  <ShieldCheck className="w-3 h-3" /> Verified
                </Badge>
              )}
              {profile.is_responsive && (
                <Badge
                  variant="outline"
                  className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs"
                  data-testid="profile-modal-responsive-badge"
                >
                  Responsive
                </Badge>
              )}
              {profile.is_premium && (
                <Badge className="bg-amber-100 text-amber-800 border border-amber-200 text-xs">
                  Premium
                </Badge>
              )}
              {profile.performance_tier && (
                <Badge className="bg-cyan-100 text-cyan-800 border border-cyan-200 text-xs capitalize">
                  {typeof profile.performance_tier === 'string'
                    ? profile.performance_tier
                    : (profile.performance_tier.label || profile.performance_tier.key || '')}
                </Badge>
              )}
            </div>
            {profile.role_name && (
              <p className="text-sm text-gray-700 mt-0.5">{profile.role_name}</p>
            )}
            {location && (
              <p className="text-xs text-gray-500 mt-1 inline-flex items-center gap-1">
                <MapPin className="w-3 h-3" /> {location}
              </p>
            )}
            <div className="flex items-center gap-4 mt-2 text-xs text-gray-700 flex-wrap">
              <span className="inline-flex items-center gap-1" data-testid="profile-modal-rating">
                <StarRow value={profile.avg_rating} />
                <span className="font-medium">{Number(profile.avg_rating || 0).toFixed(1)}</span>
                <span className="text-gray-500">({profile.rating_count || 0})</span>
              </span>
              <span className="inline-flex items-center gap-1">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                {profile.completed_gigs || 0} completed
              </span>
              {profile.reliability_percent != null && (
                <span className="inline-flex items-center gap-1" data-testid="profile-modal-reliability">
                  <ShieldCheck className="w-3.5 h-3.5 text-cyan-600" />
                  {profile.reliability_percent}% reliability
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Bio */}
        {profile.bio && (
          <Section title="About" testId="profile-modal-bio-section">
            <p className="text-sm text-gray-700 whitespace-pre-line" data-testid="profile-modal-bio">{profile.bio}</p>
          </Section>
        )}

        {/* Social Links */}
        {social.length > 0 && (
          <Section title="Social" testId="profile-modal-social-section">
            <div className="flex flex-wrap gap-2">
              {social.map((s) => {
                const Icon = s.icon;
                return (
                  <a
                    key={s.key}
                    href={s.href}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200"
                    data-testid={`profile-modal-social-${s.key}`}
                  >
                    <Icon className="w-3 h-3" />
                    {s.label}
                  </a>
                );
              })}
            </div>
          </Section>
        )}

        {/* Portfolio gallery — Iter-221: removed previous .slice(0, 8) cap
            so clients can see ALL portfolio photos a talent has uploaded
            (the cap was a UI accident, not an intentional paid-tier gate).
            Lazy-loading + responsive grid keeps performance fine even at
            20+ images. */}
        {portfolio.length > 0 && (
          <Section title={`Portfolio${portfolio.length > 1 ? ` (${portfolio.length})` : ''}`} testId="profile-modal-portfolio-section">
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="profile-modal-portfolio-grid">
              {portfolio.map((url, i) => (
                <button
                  type="button"
                  key={i}
                  onClick={() => openModalLightbox(url)}
                  className="block aspect-square overflow-hidden rounded-md bg-gray-100 cursor-zoom-in"
                  data-testid={`profile-modal-portfolio-img-${i}`}
                >
                  <img src={url} alt={`Portfolio ${i + 1}`} className="w-full h-full object-cover hover:scale-105 transition-transform" loading="lazy" />
                </button>
              ))}
            </div>
          </Section>
        )}

        {/* Work Experience / Role History */}
        {work.length > 0 && (
          <Section title="Experience" icon={Briefcase} testId="profile-modal-experience-section">
            <ul className="space-y-2">
              {work.slice(0, 6).map((w, i) => (
                <li key={i} className="text-sm">
                  <p className="font-medium text-gray-900">{w.role || w.title || w.company || 'Role'}</p>
                  {(w.company || w.organization) && (
                    <p className="text-xs text-gray-600">{w.company || w.organization}</p>
                  )}
                  {(w.dates || w.duration || w.start) && (
                    <p className="text-xs text-gray-500 inline-flex items-center gap-1">
                      <Clock className="w-3 h-3" /> {w.dates || w.duration || w.start}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          </Section>
        )}

        {/* Iter-202 — Certifications now ALWAYS render (with "No
            certifications uploaded" empty state) so admins/clients
            don't have to wonder if the data is missing vs absent. */}
        <Section title="Certifications" icon={Award} testId="profile-modal-certs-section">
          <CertificationList
            certifications={certs}
            variant="full"
            showDocLink={isAdmin}
            adminMode={isAdmin}
            onReviewed={() => {
              // Iter-227n — refetch profile so the badge + actions update.
              api.get(`/discover/talent/${talentId}`).then((r) => setProfile(r.data)).catch(() => {});
            }}
            testIdPrefix="profile-modal-cert"
            emptyMessage="No certifications uploaded yet."
          />
        </Section>

        {/* Recent reviews */}
        {reviews.length > 0 && (
          <Section title="Recent Reviews" testId="profile-modal-reviews-section">
            <ul className="space-y-2">
              {reviews.slice(0, 3).map((r, i) => (
                <li key={i} className="border border-gray-200 rounded-md p-2.5 text-sm">
                  <div className="flex items-center justify-between mb-1">
                    <StarRow value={r.rating} />
                    {r.reviewer_name && (
                      <span className="text-xs text-gray-500">— {r.reviewer_name}</span>
                    )}
                  </div>
                  {r.comment && <p className="text-xs text-gray-700">{r.comment}</p>}
                </li>
              ))}
            </ul>
          </Section>
        )}

        {/* CTA */}
        <div className="pt-2 border-t border-gray-100 flex items-center justify-between gap-3 flex-wrap">
          <Link
            to={fullProfilePath}
            onClick={() => onOpenChange?.(false)}
            className="inline-flex items-center text-sm text-cyan-700 font-medium hover:underline"
            data-testid="profile-modal-open-full"
          >
            Open full profile <ExternalLink className="w-3.5 h-3.5 ml-1" />
          </Link>
          {isAdmin && (
            <div className="flex items-center gap-2">
              <AddToInviteCartButton
                talent={{
                  user_id: profile.user_id,
                  name,
                  photo_url: profile.profile_photo_url,
                  role: profile.role_name,
                  city: profile.city,
                  state: profile.state,
                }}
                variant="pill"
                testIdSuffix="profile-modal"
              />
              <Button
                size="sm"
                className="bg-cyan-500 hover:bg-cyan-600 text-white"
                onClick={() => {
                  onOpenChange?.(false);
                  openInviteToShift(profile.user_id, name);
                }}
                data-testid="profile-modal-invite-to-gig"
              >
                <Send className="w-3.5 h-3.5 mr-1" /> Invite to Gig
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  };

  const modalPhotos = (() => {
    if (!profile) return [];
    const out = []; const seen = new Set();
    [resolveProfilePhoto(profile), ...((profile.portfolio_images || []).filter(Boolean))]
      .forEach((u) => { if (u && !seen.has(u)) { out.push(u); seen.add(u); } });
    return out;
  })();
  const openModalLightbox = (url) => {
    const i = modalPhotos.indexOf(url);
    setModalLightboxIdx(i >= 0 ? i : 0);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="talent-profile-modal">
        <DialogTitle className="sr-only">Talent profile</DialogTitle>
        <DialogDescription className="sr-only">Full talent profile including bio, ratings, experience, and certifications.</DialogDescription>
        {renderBody()}
        <ImageLightbox
          images={modalPhotos}
          index={modalLightboxIdx}
          onIndexChange={setModalLightboxIdx}
          onClose={() => setModalLightboxIdx(null)}
        />
      </DialogContent>
    </Dialog>
  );
};

export default TalentProfileModal;
